import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import type { CaseListItem } from "@/domain/case/case";

import { CaseStatusBadge } from "./case-status-badge";
import { ReturnedReasonLine } from "./returned-reason";

export type ActionRequiredSectionProps = {
  readonly items: readonly CaseListItem[];
  /** 候補を取り切れていない可能性があるか（件数に「以上」を付ける）。 */
  readonly hasMore?: boolean;
  readonly onResume?: (_caseId: number) => void;
};

/** 既定で見せる件数。ホームの RECENT_COUNT と同じ流儀でここに置く。 */
const ACTION_REQUIRED_VISIBLE = 3;

/**
 * 「自分の操作待ち」の案件を一覧の上に集約する（差し戻し・下書き。
 * 「関与した案件」では自分宛の承認待ちも先頭に入る）。
 * マイ案件を一覧だけでなく作業場にするための導線（issue #29 の確定仕様）。
 *
 * 対象は一覧の絞り込みとは独立に取得したもの（use-action-required）。一覧の結果
 * から拾うと、絞り込みやページ送りで内容が変わって誤解を招く。
 *
 * 0 件のときは何も描画しない（空セクションでスペースを取らない）。
 *
 * 件数が多いと一覧テーブルを画面外へ押し出すので、既定では先頭数件だけ出す
 * （実データで 17 件・約 800px になっていた）。総数はヘッダに出るので失われない。
 * 並び順は「承認待ち → 差し戻し → 下書き」なので、切り詰めても急ぐものが先頭に残る
 * （他人を待たせているものから片付けられる）。
 */
export function ActionRequiredSection({
  items,
  hasMore = false,
  onResume,
}: ActionRequiredSectionProps) {
  const { t } = useTranslation(["common", "domain"]);
  const [isExpanded, setIsExpanded] = useState(false);
  // 選別と並びは取得側（use-action-required）で済んでいる。ここで selectActionRequired
  // を呼び直すと、承認待ちを混ぜた結果が捨てられる
  const targets = items;

  if (targets.length === 0) return null;

  const visible = isExpanded
    ? targets
    : targets.slice(0, ACTION_REQUIRED_VISIBLE);
  const hiddenCount = targets.length - visible.length;

  return (
    <section
      aria-labelledby="action-required-heading"
      className="border-destructive/30 bg-destructive/5 flex flex-col gap-3 rounded-lg border p-4"
    >
      <header className="flex flex-col gap-0.5">
        <h2
          id="action-required-heading"
          className="flex items-center gap-2 text-sm font-semibold"
        >
          {t("common:myApplications.actionRequired")}
          <span className="text-muted-foreground font-normal tabular-nums">
            {t(
              hasMore
                ? "common:pagination.totalAtLeast"
                : "common:pagination.total",
              { count: targets.length },
            )}
          </span>
        </h2>
        <p className="text-muted-foreground text-xs">
          {t("common:myApplications.actionRequiredDescription")}
        </p>
      </header>

      <ul className="flex flex-col gap-2">
        {visible.map((item) => (
          <li
            key={item.caseId}
            className="bg-background flex flex-col gap-1 rounded-md border px-3 py-2 text-sm"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-muted-foreground tabular-nums">
                {item.caseNumber}
              </span>
              <span className="flex-1 font-medium">{item.title}</span>
              <CaseStatusBadge status={item.status} />
              <Button
                size="sm"
                variant="outline"
                onClick={onResume ? () => onResume(item.caseId) : undefined}
              >
                {item.status === "returned"
                  ? t("common:myApplications.fixAndResubmit")
                  : t("common:myApplications.editDraft")}
              </Button>
            </div>

            {/* 差し戻しは理由まで見せる（開く前にどれから直すか決められる） */}
            {item.status === "returned" ? (
              <ReturnedReasonLine metadata={item.metadata} />
            ) : null}
          </li>
        ))}
      </ul>

      {hiddenCount > 0 || isExpanded ? (
        <div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded
              ? t("common:myApplications.showLessActionRequired")
              : t("common:myApplications.showMoreActionRequired", {
                  count: hiddenCount,
                })}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
